import { useState, useEffect } from 'react'
import { Settings, Table as TableIcon, List, History, Save, Plus, Trash2, Edit2, GripVertical, AlertCircle, CheckCircle, Home, X } from 'lucide-react'
import { apiService } from '../services/api'
import LoadingSpinner from '../components/ui/LoadingSpinner'
import ErrorMessage from '../components/ui/ErrorMessage'

const AdminVisualization = () => {
  const [activeTab, setActiveTab] = useState('operator-status')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  
  // Data states
  const [operatorConfigs, setOperatorConfigs] = useState([])
  const [priorityActions, setPriorityActions] = useState([])
  const [legendItems, setLegendItems] = useState([])
  const [history, setHistory] = useState([])
  
  const [implementationOverrides, setImplementationOverrides] = useState([])

  // Edit states
  const [editingOperator, setEditingOperator] = useState(null)
  const [editingAction, setEditingAction] = useState(null)
  const [editingLegend, setEditingLegend] = useState(null)
  const [newAction, setNewAction] = useState(null)
  const [newLegend, setNewLegend] = useState(null)
  const [editingOverride, setEditingOverride] = useState(null) // { operator_code, metric_key, ... }

  useEffect(() => {
    loadData()
  }, [activeTab])

  const loadData = async () => {
    setLoading(true)
    setError(null)
    try {
      if (activeTab === 'operator-status') {
        const response = await apiService.getOperatorStatusConfigs()
        setOperatorConfigs(response.data.configs || [])
      } else if (activeTab === 'priority-actions') {
        const response = await apiService.getPriorityActions()
        setPriorityActions(response.data.actions || [])
      } else if (activeTab === 'legend') {
        const response = await apiService.getLegendItems()
        setLegendItems(response.data.items || [])
      } else if (activeTab === 'history') {
        const response = await apiService.getVisualizationHistory({ limit: 50 })
        setHistory(response.data.history || [])
      } else if (activeTab === 'implementation-overrides') {
        const response = await apiService.getImplementationOverrides()
        setImplementationOverrides(response.data.overrides || [])
      }
    } catch (err) {
      setError(`Failed to load data: ${err.response?.data?.detail || err.message}`)
    } finally {
      setLoading(false)
    }
  }

  const showSuccess = (message) => {
    setSuccess(message)
    setTimeout(() => setSuccess(null), 3000)
  }

  // ============================================================================
  // Operator Status Tab
  // ============================================================================
  const handleOperatorEdit = (operatorCode) => {
    const config = operatorConfigs.find(c => c.operator_code === operatorCode)
    setEditingOperator({ ...config })
  }

  const handleOperatorSave = async () => {
    if (!editingOperator) return
    setSaving(true)
    setError(null)
    try {
      await apiService.updateOperatorStatus(editingOperator.operator_code, {
        zatowarowanie: editingOperator.zatowarowanie,
        ekspozycja_name: editingOperator.ekspozycja_name,
        ekspozycja_value: editingOperator.ekspozycja_value,
        stock_exposition_operator: editingOperator.stock_exposition_operator,
        plan_sprzedazy_value: editingOperator.plan_sprzedazy_value,
        plan_sprzedazy_status: editingOperator.plan_sprzedazy_status,
        premiowanie_value: editingOperator.premiowanie_value,
        premiowanie_status: editingOperator.premiowanie_status,
        status: editingOperator.status,
        status_color: editingOperator.status_color
      })
      showSuccess(`Updated ${editingOperator.operator_code} successfully`)
      setEditingOperator(null)
      loadData()
    } catch (err) {
      setError(`Failed to update: ${err.response?.data?.detail || err.message}`)
    } finally {
      setSaving(false)
    }
  }

  // ============================================================================
  // Priority Actions Tab
  // ============================================================================
  const handleActionCreate = () => {
    setNewAction({
      operator: '',
      action: '',
      priority_level: 'high_priority',
      color: '#f59e0b',
      display_order: priorityActions.length
    })
  }

  const handleActionSave = async (action) => {
    setSaving(true)
    setError(null)
    try {
      if (action.id) {
        // Update existing
        await apiService.updatePriorityAction(action.id, {
          operator: action.operator,
          action: action.action,
          priority_level: action.priority_level,
          color: action.color
        })
        showSuccess('Action updated successfully')
      } else {
        // Create new
        await apiService.createPriorityAction(action)
        showSuccess('Action created successfully')
      }
      setEditingAction(null)
      setNewAction(null)
      loadData()
    } catch (err) {
      setError(`Failed to save action: ${err.response?.data?.detail || err.message}`)
    } finally {
      setSaving(false)
    }
  }

  const handleActionDelete = async (actionId) => {
    if (!confirm('Are you sure you want to delete this action?')) return
    setSaving(true)
    setError(null)
    try {
      await apiService.deletePriorityAction(actionId)
      showSuccess('Action deleted successfully')
      loadData()
    } catch (err) {
      setError(`Failed to delete action: ${err.response?.data?.detail || err.message}`)
    } finally {
      setSaving(false)
    }
  }

  // ============================================================================
  // Legend Tab
  // ============================================================================
  const handleLegendCreate = () => {
    setNewLegend({
      status_label: '',
      color: '#15803d',
      display_order: legendItems.length
    })
  }

  const handleLegendSave = async (item) => {
    setSaving(true)
    setError(null)
    try {
      if (item.id) {
        // Update existing
        await apiService.updateLegendItem(item.id, {
          status_label: item.status_label,
          color: item.color,
          display_order: item.display_order
        })
        showSuccess('Legend item updated successfully')
      } else {
        // Create new
        await apiService.createLegendItem(item)
        showSuccess('Legend item created successfully')
      }
      setEditingLegend(null)
      setNewLegend(null)
      loadData()
    } catch (err) {
      setError(`Failed to save legend item: ${err.response?.data?.detail || err.message}`)
    } finally {
      setSaving(false)
    }
  }

  const handleLegendDelete = async (itemId) => {
    if (!confirm('Are you sure you want to delete this legend item?')) return
    setSaving(true)
    setError(null)
    try {
      await apiService.deleteLegendItem(itemId)
      showSuccess('Legend item deleted successfully')
      loadData()
    } catch (err) {
      setError(`Failed to delete legend item: ${err.response?.data?.detail || err.message}`)
    } finally {
      setSaving(false)
    }
  }

  // ============================================================================
  // Render Methods
  // ============================================================================
  const renderOperatorStatusTab = () => {
    if (loading) return <LoadingSpinner />

    return (
      <div className="space-y-4">
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-blue-600 text-white">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium">Operator</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Zatowarowanie</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Ekspozycja</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Plan Sprzedaży</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Premiowanie</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Status</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {operatorConfigs.map(config => (
                <tr key={config.operator_code} className="border-b border-gray-200 hover:bg-gray-50">
                  <td className="px-4 py-3 font-semibold">{config.operator_code}</td>
                  <td className="px-4 py-3 text-sm">{config.zatowarowanie || '-'}</td>
                  <td className="px-4 py-3 text-sm">
                    {config.ekspozycja_name && <div>{config.ekspozycja_name}</div>}
                    {config.ekspozycja_value && <div className="text-gray-600">{config.ekspozycja_value}</div>}
                    {!config.ekspozycja_name && !config.ekspozycja_value && '-'}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {config.plan_sprzedazy_value || '-'}
                    {config.plan_sprzedazy_status && (
                      <span className={`ml-2 px-2 py-1 rounded text-xs ${
                        config.plan_sprzedazy_status === 'high' ? 'bg-green-100 text-green-800' :
                        config.plan_sprzedazy_status === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>{config.plan_sprzedazy_status}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {config.premiowanie_value || '-'}
                    {config.premiowanie_status && (
                      <span className={`ml-2 px-2 py-1 rounded text-xs ${
                        config.premiowanie_status === 'high' ? 'bg-green-100 text-green-800' :
                        config.premiowanie_status === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>{config.premiowanie_status}</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span 
                      className="px-3 py-1 rounded-full text-xs font-medium text-white"
                      style={{ backgroundColor: config.status_color }}
                    >
                      {config.status || '-'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => handleOperatorEdit(config.operator_code)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Edit Modal */}
        {editingOperator && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <h3 className="text-lg font-semibold mb-4">Edit {editingOperator.operator_code}</h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Zatowarowanie</label>
                    <input
                      type="text"
                      value={editingOperator.zatowarowanie || ''}
                      onChange={(e) => setEditingOperator({...editingOperator, zatowarowanie: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Ekspozycja Name</label>
                      <input
                        type="text"
                        value={editingOperator.ekspozycja_name || ''}
                        onChange={(e) => setEditingOperator({...editingOperator, ekspozycja_name: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Ekspozycja Value</label>
                      <input
                        type="text"
                        value={editingOperator.ekspozycja_value || ''}
                        onChange={(e) => setEditingOperator({...editingOperator, ekspozycja_value: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Towar+Ekspozycja (operator)</label>
                    <input
                      type="text"
                      value={editingOperator.stock_exposition_operator || ''}
                      onChange={(e) => setEditingOperator({...editingOperator, stock_exposition_operator: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      placeholder="np. 85/120 (71%)"
                    />
                    <p className="mt-1 text-sm text-gray-500">Dane statyczne od operatora o ilości salonów z towarem i ekspozycją</p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Plan Sprzedaży Value</label>
                      <input
                        type="text"
                        value={editingOperator.plan_sprzedazy_value || ''}
                        onChange={(e) => setEditingOperator({...editingOperator, plan_sprzedazy_value: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Plan Sprzedaży Status</label>
                      <select
                        value={editingOperator.plan_sprzedazy_status || ''}
                        onChange={(e) => setEditingOperator({...editingOperator, plan_sprzedazy_status: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      >
                        <option value="">None</option>
                        <option value="high">High</option>
                        <option value="medium">Medium</option>
                        <option value="low">Low</option>
                      </select>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Premiowanie Value</label>
                      <input
                        type="text"
                        value={editingOperator.premiowanie_value || ''}
                        onChange={(e) => setEditingOperator({...editingOperator, premiowanie_value: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Premiowanie Status</label>
                      <select
                        value={editingOperator.premiowanie_status || ''}
                        onChange={(e) => setEditingOperator({...editingOperator, premiowanie_status: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      >
                        <option value="">None</option>
                        <option value="high">High</option>
                        <option value="medium">Medium</option>
                        <option value="low">Low</option>
                      </select>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                      <input
                        type="text"
                        value={editingOperator.status || ''}
                        onChange={(e) => setEditingOperator({...editingOperator, status: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        placeholder="DOSKONAŁY, W TRAKCIE, etc."
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Status Color</label>
                      <div className="flex space-x-2">
                        <input
                          type="color"
                          value={editingOperator.status_color || '#15803d'}
                          onChange={(e) => setEditingOperator({...editingOperator, status_color: e.target.value})}
                          className="h-10 w-20 cursor-pointer"
                        />
                        <input
                          type="text"
                          value={editingOperator.status_color || '#15803d'}
                          onChange={(e) => setEditingOperator({...editingOperator, status_color: e.target.value})}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                          placeholder="#15803d"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    onClick={() => setEditingOperator(null)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                    disabled={saving}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleOperatorSave}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center space-x-2"
                    disabled={saving}
                  >
                    {saving ? <LoadingSpinner size="sm" /> : <Save className="h-4 w-4" />}
                    <span>Save</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }

  const renderPriorityActionsTab = () => {
    if (loading) return <LoadingSpinner />

    const actionForm = (action, isNew = false) => (
      <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Operator</label>
          <input
            type="text"
            value={action.operator}
            onChange={(e) => isNew ? 
              setNewAction({...action, operator: e.target.value}) : 
              setEditingAction({...action, operator: e.target.value})
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
            placeholder="PLUS, PLAY, T-MOBILE & ORANGE, etc."
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Action</label>
          <textarea
            value={action.action}
            onChange={(e) => isNew ? 
              setNewAction({...action, action: e.target.value}) : 
              setEditingAction({...action, action: e.target.value})
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
            rows="2"
            placeholder="Description of the action..."
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Priority Level</label>
            <select
              value={action.priority_level}
              onChange={(e) => isNew ? 
                setNewAction({...action, priority_level: e.target.value}) : 
                setEditingAction({...action, priority_level: e.target.value})
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            >
              <option value="high_priority">High Priority</option>
              <option value="standard_priority">Standard Priority</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
            <div className="flex space-x-2">
              <input
                type="color"
                value={action.color}
                onChange={(e) => isNew ? 
                  setNewAction({...action, color: e.target.value}) : 
                  setEditingAction({...action, color: e.target.value})
                }
                className="h-10 w-16 cursor-pointer"
              />
              <input
                type="text"
                value={action.color}
                onChange={(e) => isNew ? 
                  setNewAction({...action, color: e.target.value}) : 
                  setEditingAction({...action, color: e.target.value})
                }
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
          </div>
        </div>
        <div className="flex justify-end space-x-2">
          <button
            onClick={() => isNew ? setNewAction(null) : setEditingAction(null)}
            className="px-3 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            disabled={saving}
          >
            Cancel
          </button>
          <button
            onClick={() => handleActionSave(action)}
            className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center space-x-2"
            disabled={saving}
          >
            {saving ? <LoadingSpinner size="sm" /> : <Save className="h-4 w-4" />}
            <span>Save</span>
          </button>
        </div>
      </div>
    )

    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold">Priority Actions</h3>
          {!newAction && (
            <button
              onClick={handleActionCreate}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center space-x-2"
            >
              <Plus className="h-4 w-4" />
              <span>Add Action</span>
            </button>
          )}
        </div>

        {newAction && (
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">New Action</h4>
            {actionForm(newAction, true)}
          </div>
        )}

        <div className="space-y-3">
          {priorityActions.filter(a => a.is_active).map(action => (
            <div key={action.id}>
              {editingAction?.id === action.id ? (
                actionForm(editingAction, false)
              ) : (
                <div className="bg-white rounded-lg border border-gray-200 p-4 flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <div 
                        className="w-4 h-4 rounded"
                        style={{ backgroundColor: action.color }}
                      ></div>
                      <span className="font-medium">{action.operator}</span>
                      <span className={`px-2 py-1 rounded text-xs ${
                        action.priority_level === 'high_priority' ? 
                        'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'
                      }`}>
                        {action.priority_level === 'high_priority' ? 'High' : 'Standard'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-700">{action.action}</p>
                  </div>
                  <div className="flex space-x-2 ml-4">
                    <button
                      onClick={() => setEditingAction({...action})}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleActionDelete(action.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    )
  }

  const renderLegendTab = () => {
    if (loading) return <LoadingSpinner />

    const legendForm = (item, isNew = false) => (
      <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Status Label</label>
          <input
            type="text"
            value={item.status_label}
            onChange={(e) => isNew ? 
              setNewLegend({...item, status_label: e.target.value}) : 
              setEditingLegend({...item, status_label: e.target.value})
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
            placeholder="DOSKONAŁY, DOBRY, etc."
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
          <div className="flex space-x-2">
            <input
              type="color"
              value={item.color}
              onChange={(e) => isNew ? 
                setNewLegend({...item, color: e.target.value}) : 
                setEditingLegend({...item, color: e.target.value})
              }
              className="h-10 w-20 cursor-pointer"
            />
            <input
              type="text"
              value={item.color}
              onChange={(e) => isNew ? 
                setNewLegend({...item, color: e.target.value}) : 
                setEditingLegend({...item, color: e.target.value})
              }
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
              placeholder="#15803d"
            />
          </div>
        </div>
        <div className="flex justify-end space-x-2">
          <button
            onClick={() => isNew ? setNewLegend(null) : setEditingLegend(null)}
            className="px-3 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            disabled={saving}
          >
            Cancel
          </button>
          <button
            onClick={() => handleLegendSave(item)}
            className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center space-x-2"
            disabled={saving}
          >
            {saving ? <LoadingSpinner size="sm" /> : <Save className="h-4 w-4" />}
            <span>Save</span>
          </button>
        </div>
      </div>
    )

    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold">Legend Items</h3>
          {!newLegend && (
            <button
              onClick={handleLegendCreate}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center space-x-2"
            >
              <Plus className="h-4 w-4" />
              <span>Add Legend Item</span>
            </button>
          )}
        </div>

        {newLegend && (
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">New Legend Item</h4>
            {legendForm(newLegend, true)}
          </div>
        )}

        <div className="space-y-3">
          {legendItems.filter(item => item.is_active).map(item => (
            <div key={item.id}>
              {editingLegend?.id === item.id ? (
                legendForm(editingLegend, false)
              ) : (
                <div className="bg-white rounded-lg border border-gray-200 p-4 flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div 
                      className="w-8 h-8 rounded"
                      style={{ backgroundColor: item.color }}
                    ></div>
                    <span className="font-medium">{item.status_label}</span>
                    <span className="text-sm text-gray-500">{item.color}</span>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setEditingLegend({...item})}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleLegendDelete(item.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    )
  }

  const renderHistoryTab = () => {
    if (loading) return <LoadingSpinner />

    return (
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Change History</h3>
        
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-700">Timestamp</th>
                <th className="px-4 py-3 text-left font-medium text-gray-700">Type</th>
                <th className="px-4 py-3 text-left font-medium text-gray-700">Change</th>
                <th className="px-4 py-3 text-left font-medium text-gray-700">User ID</th>
              </tr>
            </thead>
            <tbody>
              {history.length === 0 ? (
                <tr>
                  <td colSpan="4" className="px-4 py-8 text-center text-gray-500">
                    No history records found
                  </td>
                </tr>
              ) : (
                history.map(record => (
                  <tr key={record.id} className="border-t border-gray-200 hover:bg-gray-50">
                    <td className="px-4 py-3">
                      {new Date(record.changed_at).toLocaleString()}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        record.change_type === 'CREATE' ? 'bg-green-100 text-green-800' :
                        record.change_type === 'UPDATE' ? 'bg-blue-100 text-blue-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {record.change_type}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium">{record.config_type}</div>
                      {record.entity_id && (
                        <div className="text-xs text-gray-500">ID: {record.entity_id}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {record.changed_by || 'System'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    )
  }

  // ============================================================================
  // Implementation Overrides Tab
  // ============================================================================

  const OPERATORS = ['ORANGE', 'TMOBILE', 'PLUS', 'P4']
  const OPERATOR_LABELS = { ORANGE: 'Orange', TMOBILE: 'T-Mobile', PLUS: 'Plus', P4: 'Play' }
  const OPERATOR_COLORS = { ORANGE: '#FF7900', TMOBILE: '#E20074', PLUS: '#00A651', P4: '#9333EA' }

  const METRIC_KEYS = [
    'ekspozycja', 'towar', 'towar_ekspozycja', 'live_demo', 'ambassador_device'
  ]
  const METRIC_LABELS = {
    ekspozycja: 'Ekspozycja',
    towar: 'Towar',
    towar_ekspozycja: 'Towar + Ekspozycja',
    live_demo: 'Live Demo',
    ambassador_device: 'Urz. ambasadorskie'
  }

  const getOverrideForCell = (operatorCode, metricKey) => {
    return implementationOverrides.find(
      o => o.operator_code === operatorCode && o.metric_key === metricKey && o.is_active
    )
  }

  const handleOverrideSave = async () => {
    if (!editingOverride) return
    setSaving(true)
    setError(null)
    try {
      const { operator_code, metric_key, override_type, fixed_value_int, fixed_total_int, fixed_value_str } = editingOverride

      if (override_type === 'none') {
        // Delete override if it exists
        const existing = getOverrideForCell(operator_code, metric_key)
        if (existing) {
          await apiService.deleteImplementationOverride(existing.id)
        }
      } else {
        await apiService.createImplementationOverride({
          operator_code,
          metric_key,
          override_type,
          fixed_value_int: override_type === 'fixed_value' ? fixed_value_int : null,
          fixed_total_int: override_type === 'fixed_value' ? fixed_total_int : null,
          fixed_value_str: null
        })
      }

      setEditingOverride(null)
      setSuccess('Override saved')
      setTimeout(() => setSuccess(null), 3000)
      loadData()
    } catch (err) {
      setError(`Failed to save override: ${err.response?.data?.detail || err.message}`)
    } finally {
      setSaving(false)
    }
  }

  const openOverrideEditor = (operatorCode, metricKey) => {
    const existing = getOverrideForCell(operatorCode, metricKey)
    if (existing) {
      setEditingOverride({
        operator_code: operatorCode,
        metric_key: metricKey,
        override_type: existing.override_type,
        fixed_value_int: existing.fixed_value_int,
        fixed_total_int: existing.fixed_total_int,
        fixed_value_str: existing.fixed_value_str
      })
    } else {
      setEditingOverride({
        operator_code: operatorCode,
        metric_key: metricKey,
        override_type: 'none',
        fixed_value_int: null,
        fixed_total_int: null,
        fixed_value_str: null
      })
    }
  }

  const renderCellContent = (operatorCode, metricKey) => {
    const override = getOverrideForCell(operatorCode, metricKey)
    if (!override) {
      return <span className="text-gray-400 text-xs">Brak</span>
    }
    if (override.override_type === 'not_applicable') {
      return <span className="inline-block px-2 py-0.5 bg-gray-200 text-gray-600 text-xs rounded font-medium">N/A</span>
    }
    if (override.override_type === 'fixed_value') {
      const pct = override.fixed_total_int > 0
        ? Math.round((override.fixed_value_int / override.fixed_total_int) * 100)
        : 0
      return (
        <span className="text-xs font-medium text-blue-700">
          {override.fixed_value_int}/{override.fixed_total_int} ({pct}%)
        </span>
      )
    }
    return null
  }

  const renderImplementationOverridesTab = () => {
    if (loading) return <LoadingSpinner />

    return (
      <div>
        <div className="mb-4">
          <p className="text-sm text-gray-600">
            Kliknij komórkę, aby nadpisać wskaźnik dla wybranego operatora.
            Możesz ustawić "Nie dotyczy" lub wprowadzić stałą wartość.
          </p>
        </div>

        {/* Matrix Grid */}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th className="text-left text-xs font-semibold text-gray-500 uppercase px-3 py-2 border-b border-gray-200 w-48">
                  Wskaźnik
                </th>
                {OPERATORS.map(op => (
                  <th key={op} className="text-center text-xs font-semibold uppercase px-3 py-2 border-b border-gray-200" style={{ color: OPERATOR_COLORS[op] }}>
                    {OPERATOR_LABELS[op]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {METRIC_KEYS.map((metricKey) => (
                <tr key={metricKey}>
                  <td className="text-xs font-medium text-gray-700 px-3 py-2.5 border-b border-gray-100">
                    {METRIC_LABELS[metricKey]}
                  </td>
                  {OPERATORS.map(op => (
                    <td
                      key={`${op}-${metricKey}`}
                      onClick={() => openOverrideEditor(op, metricKey)}
                      className="text-center px-3 py-2.5 border-b border-gray-100 cursor-pointer hover:bg-blue-50 transition-colors"
                    >
                      {renderCellContent(op, metricKey)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Edit Modal */}
        {editingOverride && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md mx-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Override: {METRIC_LABELS[editingOverride.metric_key]}
                </h3>
                <button onClick={() => setEditingOverride(null)} className="text-gray-400 hover:text-gray-600">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <p className="text-sm text-gray-500 mb-4" style={{ color: OPERATOR_COLORS[editingOverride.operator_code] }}>
                {OPERATOR_LABELS[editingOverride.operator_code]}
              </p>

              {/* Override Type Selection */}
              <div className="space-y-2 mb-4">
                {[
                  { value: 'none', label: 'Brak nadpisania (dane live)' },
                  { value: 'not_applicable', label: 'Nie dotyczy (N/A)' },
                  { value: 'fixed_value', label: 'Stała wartość' }
                ].map(option => (
                  <label key={option.value} className="flex items-center space-x-3 p-2 rounded hover:bg-gray-50 cursor-pointer">
                    <input
                      type="radio"
                      name="override_type"
                      value={option.value}
                      checked={editingOverride.override_type === option.value}
                      onChange={() => setEditingOverride({ ...editingOverride, override_type: option.value })}
                      className="text-blue-600"
                    />
                    <span className="text-sm text-gray-700">{option.label}</span>
                  </label>
                ))}
              </div>

              {/* Fixed Value Inputs */}
              {editingOverride.override_type === 'fixed_value' && (
                <div className="border-t pt-4 space-y-3">
                  <div className="flex space-x-3">
                    <div className="flex-1">
                      <label className="block text-xs font-medium text-gray-600 mb-1">Wartość (licznik)</label>
                      <input
                        type="number"
                        min="0"
                        value={editingOverride.fixed_value_int ?? ''}
                        onChange={(e) => setEditingOverride({ ...editingOverride, fixed_value_int: e.target.value ? parseInt(e.target.value) : null })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="np. 85"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="block text-xs font-medium text-gray-600 mb-1">Total (mianownik)</label>
                      <input
                        type="number"
                        min="1"
                        value={editingOverride.fixed_total_int ?? ''}
                        onChange={(e) => setEditingOverride({ ...editingOverride, fixed_total_int: e.target.value ? parseInt(e.target.value) : null })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="np. 120"
                      />
                    </div>
                  </div>
                  {editingOverride.fixed_value_int != null && editingOverride.fixed_total_int > 0 && (
                    <p className="text-xs text-gray-500">
                      Podgląd: {editingOverride.fixed_value_int}/{editingOverride.fixed_total_int} ({Math.round((editingOverride.fixed_value_int / editingOverride.fixed_total_int) * 100)}%)
                    </p>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setEditingOverride(null)}
                  className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Anuluj
                </button>
                <button
                  onClick={handleOverrideSave}
                  disabled={saving}
                  className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2"
                >
                  <Save className="h-4 w-4" />
                  <span>{saving ? 'Zapisywanie...' : 'Zapisz'}</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center space-x-3 mb-2">
          <Settings className="h-6 w-6 text-blue-600" />
          <h1 className="text-2xl font-bold text-gray-900">Visualization Configuration</h1>
        </div>
        <p className="text-gray-600">
          Manage operator status table, priority actions, and legend configuration
        </p>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="mb-4 bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg flex items-center space-x-2">
          <CheckCircle className="h-5 w-5" />
          <span>{success}</span>
        </div>
      )}
      {error && (
        <div className="mb-4">
          <ErrorMessage message={error} />
        </div>
      )}

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <div className="flex space-x-6">
          <button
            onClick={() => setActiveTab('operator-status')}
            className={`px-4 py-3 font-medium text-sm border-b-2 transition-colors flex items-center space-x-2 ${
              activeTab === 'operator-status'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <TableIcon className="h-4 w-4" />
            <span>Operator Status</span>
          </button>
          <button
            onClick={() => setActiveTab('priority-actions')}
            className={`px-4 py-3 font-medium text-sm border-b-2 transition-colors flex items-center space-x-2 ${
              activeTab === 'priority-actions'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <List className="h-4 w-4" />
            <span>Priority Actions</span>
          </button>
          <button
            onClick={() => setActiveTab('legend')}
            className={`px-4 py-3 font-medium text-sm border-b-2 transition-colors flex items-center space-x-2 ${
              activeTab === 'legend'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Settings className="h-4 w-4" />
            <span>Legend</span>
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`px-4 py-3 font-medium text-sm border-b-2 transition-colors flex items-center space-x-2 ${
              activeTab === 'history'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <History className="h-4 w-4" />
            <span>History</span>
          </button>
          <button
            onClick={() => setActiveTab('implementation-overrides')}
            className={`px-4 py-3 font-medium text-sm border-b-2 transition-colors flex items-center space-x-2 ${
              activeTab === 'implementation-overrides'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Home className="h-4 w-4" />
            <span>Implementacja KAM</span>
          </button>
        </div>
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === 'operator-status' && renderOperatorStatusTab()}
        {activeTab === 'priority-actions' && renderPriorityActionsTab()}
        {activeTab === 'legend' && renderLegendTab()}
        {activeTab === 'history' && renderHistoryTab()}
        {activeTab === 'implementation-overrides' && renderImplementationOverridesTab()}
      </div>
    </div>
  )
}

export default AdminVisualization

